import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  Divider,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded'
import CheckroomRoundedIcon from '@mui/icons-material/CheckroomRounded'
import FlightRoundedIcon from '@mui/icons-material/FlightRounded'
import EmailRoundedIcon from '@mui/icons-material/EmailRounded'
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded'
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppData } from '../contexts/AppDataContext'
import { useAttendeeAuth } from '../contexts/AttendeeAuthContext'
import NotifyOptIn from '../components/NotifyOptIn'
import ProfileAvatar from '../components/ProfileAvatar'
import PageContainer from '../components/ui/PageContainer'
import { LoadingState } from '../components/ui/States'
import { formatDateRange } from '../utils/format'
import { networkingService, type MyProfile } from '../services/networkingService'

export default function ProfilePage() {
  const { attendee, event } = useAppData()
  const { logout, isAuthenticated } = useAttendeeAuth()
  const navigate = useNavigate()
  const [profile, setProfile] = useState<MyProfile | null>(null)

  useEffect(() => {
    if (!isAuthenticated) return
    networkingService.getProfile().then(setProfile).catch(() => setProfile(null))
  }, [isAuthenticated])

  const handleSignOut = async () => {
    await logout()
    navigate('/')
  }

  if (!attendee || !event) return <LoadingState />

  return (
    <PageContainer title="Event Info" maxWidth="md">
      {/* Attendee card */}
      <Card variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 4 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <ProfileAvatar
            avatar={profile ?? { initials: attendee.initials }}
            name={attendee.name}
            size={64}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700}>
              {attendee.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {profile?.headline || attendee.role}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {attendee.company}
            </Typography>
            {profile?.bio && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {profile.bio}
              </Typography>
            )}
          </Box>
        </Stack>
      </Card>

      {/* Venue & logistics */}
      <Typography variant="h6" fontWeight={700} gutterBottom>
        Venue & logistics
      </Typography>
      <Card variant="outlined" sx={{ borderRadius: 3, mb: 4 }}>
        <Stack divider={<Divider />}>
          <InfoRow
            icon={<PlaceRoundedIcon color="primary" />}
            title={event.venue.name}
            body={`${event.venue.address}, ${event.venue.city}`}
            action={
              event.venue.mapUrl ? (
                <Button
                  size="small"
                  href={event.venue.mapUrl}
                  target="_blank"
                  rel="noopener"
                >
                  Map
                </Button>
              ) : undefined
            }
          />
          <InfoRow
            icon={<FlightRoundedIcon color="primary" />}
            title="Travel & parking"
            body={event.travel}
          />
          <InfoRow
            icon={<CheckroomRoundedIcon color="primary" />}
            title="Dress code"
            body={event.dressCode}
          />
        </Stack>
      </Card>

      {/* Dates */}
      <Card
        variant="outlined"
        sx={{
          p: 2.5,
          borderRadius: 3,
          mb: 4,
          bgcolor: 'rgba(0,117,188,0.05)',
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          DATES
        </Typography>
        <Typography variant="subtitle1" fontWeight={700}>
          {formatDateRange(event.startsAt, event.endsAt)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {event.timezone.replace('_', ' ')}
        </Typography>
      </Card>

      {/* FAQ */}
      <Typography variant="h6" fontWeight={700} gutterBottom>
        Frequently asked
      </Typography>
      <Box sx={{ mb: 4 }}>
        {event.faqs.map((faq, i) => (
          <Accordion
            key={i}
            disableGutters
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '12px !important',
              mb: 1,
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
              <Typography fontWeight={600}>{faq.question}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">
                {faq.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* Contacts */}
      <Typography variant="h6" fontWeight={700} gutterBottom>
        Key contacts
      </Typography>
      <Stack spacing={1.5} sx={{ mb: 4 }}>
        {event.contacts.map((c) => (
          <Card key={c.email} variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {c.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {c.role}
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <EmailRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <MuiLink href={`mailto:${c.email}`} variant="body2" underline="hover">
                  {c.email}
                </MuiLink>
              </Stack>
              {c.phone && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <PhoneRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <MuiLink href={`tel:${c.phone}`} variant="body2" underline="hover">
                    {c.phone}
                  </MuiLink>
                </Stack>
              )}
            </Stack>
          </Card>
        ))}
      </Stack>

      <NotifyOptIn />

      <Button
        variant="outlined"
        color="inherit"
        startIcon={<LogoutRoundedIcon />}
        onClick={handleSignOut}
        sx={{ color: 'text.secondary', borderColor: 'divider' }}
      >
        Sign out
      </Button>
    </PageContainer>
  )
}

function InfoRow({
  icon,
  title,
  body,
  action,
}: {
  icon: React.ReactNode
  title: string
  body?: string
  action?: React.ReactNode
}) {
  if (!body) return null
  return (
    <Box sx={{ p: 2.5, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      <Box sx={{ mt: 0.25 }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {body}
        </Typography>
      </Box>
      {action}
    </Box>
  )
}
